import { deadAfterFor, waitingPatch } from './asyncPlay.js';
import {
  ref as dbRef, onValue, onDisconnect, push, set, get, update, remove, runTransaction,
} from 'firebase/database';
import { db } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { dispatch } from '../engine/actions/turnActions';
import { createGameState } from '../engine/state/createGameState';
import { wordsForMeta } from '../engine/wordSets';
import { TEAMS } from '../engine/constants';

export const GAMES = 'codenamesGames';
export const CODES = 'codenamesGameCodes';
export const USER_GAMES = 'codenamesUserGames';

// Rooms clean themselves up, same arrangement as every sibling: presence
// plus an age limit, swept by each client for its own rooms on lobby open.
export const DEAD_AFTER_MS = {
  lobby: 60 * 60 * 1000,
  playing: 24 * 60 * 60 * 1000,
  finished: 6 * 60 * 60 * 1000,
};

// State is a JSON STRING (RTDB drops empty arrays - the house lesson), in
// one node, rewritten per action: the whole state is a couple of KB and
// moves are rare, the no-thanks shape rather than telestrations'.
const encode = (state) => JSON.stringify(state);
const decode = (raw) => {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
};

/**
 * Commits one action through the engine, atomically, WITHOUT a seq
 * precondition - the incan-gold lesson, not the no-thanks one. Two
 * operatives on the same team tap cards in the same instant here; with an
 * expectedSeq guard the first tap wins and the second is silently dropped,
 * which in incan-gold left players wedged on "thinking..." forever. The
 * transaction already re-runs `dispatch` against whatever is actually
 * stored, and every action self-guards (an invalid or stale move returns
 * the state unchanged, which aborts as a no-op).
 */
export const commitAction = async ({ gameId, action }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const stateRef = dbRef(db, `${GAMES}/${gameId}/state`);
  // Warm the sync tree with a LISTENER, not a get() - a transaction handler
  // fed local null aborts permanently (see telestrations' advanceRound for
  // the verified writeup). bindRoom normally holds one already.
  let detach = () => {};
  await new Promise((resolve) => { detach = onValue(stateRef, () => resolve()); });
  const result = await runTransaction(stateRef, (raw) => {
    const current = decode(raw);
    if (!current) return raw;
    const next = dispatch(current, action);
    if (next === current || next.meta.seq === current.meta.seq) return undefined; // no-op
    return encode(next);
  });
  detach();
  if (result.committed) {
    update(dbRef(db, `${GAMES}/${gameId}/meta`), { lastActiveAt: Date.now() }).catch(() => {});
  }
  return { committed: result.committed, state: decode(result.snapshot.val()) };
};

export { decode as decodeState };

// ---- seats -----------------------------------------------------------------

export const slotOf = (meta, uid) => (meta?.playerSlots || []).find((s) => s.uid === uid) || null;

const normalized = (slots) => slots.map((s, i) => ({ ...s, seatIndex: i }));

/**
 * Sets my team and role in the lobby. "Each team chooses one player to be
 * their spymaster" (rules p2) - one per team, so claiming the post bumps
 * the current holder to operative rather than refusing (friendlier for a
 * family lobby than an error).
 */
export const setMySlot = async ({ user, gameId, meta, team, role }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.status !== 'lobby') throw new Error('The game has already started.');
  const slots = (meta.playerSlots || []).map((s) => {
    if (s.uid === user.uid) return { ...s, team, role };
    if (role === 'spymaster' && s.team === team && s.role === 'spymaster') {
      return { ...s, role: 'operative' };
    }
    return s;
  });
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    playerSlots: normalized(slots), lastActiveAt: Date.now(),
  });
};

/**
 * Picks a curated word set for the table. Host only, any time before (or
 * between) games - the set is read at deal time.
 */
export const setWordSet = async ({ user, gameId, meta, key }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host picks the words.');
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { wordSet: key, lastActiveAt: Date.now() });
};

/**
 * Stores a generated custom set on the room. The list rides as a JSON
 * string (arrays over RTDB, the house rule) with the prompt kept as its
 * label so the lobby can say what the table is in for.
 */
export const setCustomWords = async ({ user, gameId, meta, label, words }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host picks the words.');
  if (!Array.isArray(words) || words.length < 25) throw new Error('That set came back too small.');
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    wordSet: 'custom',
    customLabel: String(label || 'Custom').slice(0, 60),
    customWords: JSON.stringify(words.slice(0, 120)),
    lastActiveAt: Date.now(),
  });
};

/** Why the table can't start yet, or null when it can (rules p2: each team
 * needs a spymaster and at least one field operative). */
export const startProblem = (meta) => {
  const slots = meta?.playerSlots || [];
  for (const team of TEAMS) {
    const mine = slots.filter((s) => s.team === team);
    if (!mine.some((s) => s.role === 'spymaster')) return `${team === 'red' ? 'Red' : 'Blue'} needs a spymaster.`;
    if (!mine.some((s) => s.role === 'operative')) return `${team === 'red' ? 'Red' : 'Blue'} needs a field operative.`;
  }
  return null;
};

// ---- room lifecycle --------------------------------------------------------

export const lastActiveOf = (meta) => {
  if (Number.isFinite(meta?.lastActiveAt)) return meta.lastActiveAt;
  const parsed = Date.parse(meta?.createdAt || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDeadRoom = (meta, presence, now = Date.now()) => {
  if (!meta) return false;
  if (Object.keys(presence || {}).length > 0) return false;
  // An async room (asyncPlay.js) is meant to sit for days; it gets the long limits.
  const limit = deadAfterFor(meta, DEAD_AFTER_MS);
  return now - lastActiveOf(meta) > limit;
};

/**
 * Codenames' async adapter (asyncPlay.js): a clue waits on the team's
 * spymaster; guesses wait on the team's operatives. A bot's post waits on
 * the lowest-seat human, who drives bots when they open the room.
 */
const lowestHuman = (g) => {
  const humans = (g?.players || []).filter((p) => !p.isBot);
  return humans.length ? [humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id] : [];
};
export const asyncAdapter = {
  waitingOnOf: (g) => {
    if (!g || g.meta.phase !== 'playing') return [];
    const role = g.turn.phase === 'clue' ? 'spymaster' : 'operative';
    const posts = g.players.filter((p) => p.team === g.turn.team && p.role === role);
    const humans = posts.filter((p) => !p.isBot).map((p) => p.id);
    return humans.length ? humans : (posts.length ? lowestHuman(g) : []);
  },
  isOver: (g) => g?.meta?.phase === 'finished',
};

/** After a move in an async room: who the game waits on now (asyncPlay.js). */
export const recordWaiting = async ({ gameId, gameState, meta }) => {
  if (!db || !meta?.async || !gameState) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export const createRoom = async ({ user, async: asyncRoom = false }) => {
  if (!db) throw new Error('Online play needs Firebase configured (see .env.example).');
  if (!user) throw new Error('Sign in first.');

  const gameRef = push(dbRef(db, GAMES));
  const gameId = gameRef.key;

  let code;
  let attempts = 0;
  do {
    code = generateRoomCode(attempts);
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
  } while (attempts < 12 && (await get(dbRef(db, `${CODES}/${code}`))).exists());

  const meta = {
    hostUid: user.uid,
    status: 'lobby',
    async: asyncRoom,
    roomCode: code,
    createdAt: new Date().toISOString(),
    lastActiveAt: Date.now(),
    playerSlots: [{
      uid: user.uid, displayName: user.displayName || 'Host', seatIndex: 0, team: 'red', role: 'spymaster',
    }],
  };
  await set(dbRef(db, `${GAMES}/${gameId}/meta`), meta);
  await set(dbRef(db, `${CODES}/${code}`), gameId);
  await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });
  return { gameId, code };
};

export const joinRoom = async ({ user, code }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (!user) throw new Error('Sign in first.');

  const normalizedCode = normalizeRoomCode(code);
  const codeSnap = await get(dbRef(db, `${CODES}/${normalizedCode}`));
  if (!codeSnap.exists()) throw new Error(`No game found for code "${normalizedCode}".`);
  const gameId = codeSnap.val();

  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) throw new Error('That game no longer exists.');
  const meta = metaSnap.val();
  const slots = meta.playerSlots || [];

  if (!slots.some((s) => s.uid === user.uid)) {
    // Rejoining a seat you hold is always fine (that's reconnecting); a NEW
    // player can't be seated mid-game - the key was dealt without them.
    if (meta.status !== 'lobby') throw new Error('That game has already started.');
    // Late joiners land as operatives on the thinner team.
    const counts = Object.fromEntries(TEAMS.map((t) => [t, slots.filter((s) => s.team === t).length]));
    const team = counts.red <= counts.blue ? 'red' : 'blue';
    await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
      playerSlots: normalized([...slots, {
        uid: user.uid, displayName: user.displayName || 'Guest', team, role: 'operative',
      }]),
      lastActiveAt: Date.now(),
    });
    await set(dbRef(db, `${USER_GAMES}/${user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
  }
  return { gameId };
};

export const leaveLobby = async ({ user, gameId }) => {
  if (!db || !user || !gameId) return;
  const metaSnap = await get(dbRef(db, `${GAMES}/${gameId}/meta`));
  if (!metaSnap.exists()) return;
  const meta = metaSnap.val();
  if (meta.status !== 'lobby') return;
  const slots = normalized((meta.playerSlots || []).filter((s) => s.uid !== user.uid));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { playerSlots: slots });
  await remove(dbRef(db, `${GAMES}/${gameId}/presence/${user.uid}`));
};

/** Deal and go. Also used for "play again": a fresh seed, same table. */
export const startRoom = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can start.');
  const problem = startProblem(meta);
  if (problem) throw new Error(problem);

  const players = [...(meta.playerSlots || [])]
    .sort((a, b) => a.seatIndex - b.seatIndex)
    .map((s, i) => ({
      id: s.uid, name: s.displayName, seat: i, isBot: Boolean(s.isBot), team: s.team, role: s.role,
    }));
  const state = createGameState({ players, seed: Date.now() | 0, words: wordsForMeta(meta) });
  await set(dbRef(db, `${GAMES}/${gameId}/state`), encode(state));
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), {
    status: 'playing',
    lastActiveAt: Date.now(),
    // An async room says who it waits on from the first turn (asyncPlay.js).
    ...(meta.async ? waitingPatch(state, meta, asyncAdapter) : {}),
  });
};

export const backToLobby = async ({ user, gameId, meta }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  if (meta.hostUid !== user?.uid) throw new Error('Only the host can do that.');
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { status: 'lobby', lastActiveAt: Date.now() });
  await remove(dbRef(db, `${GAMES}/${gameId}/state`));
};

export const markFinished = async ({ gameId }) => {
  if (!db) return;
  await update(dbRef(db, `${GAMES}/${gameId}/meta`), { status: 'finished', lastActiveAt: Date.now() })
    .catch(() => {});
};

export const bindRoom = ({ gameId, user, onMeta, onState, onPresence }) => {
  if (!db) throw new Error('Online play needs Firebase configured.');
  const at = (path) => dbRef(db, `${GAMES}/${gameId}/${path}`);
  const offs = [
    onValue(at('meta'), (s) => onMeta(s.val())),
    onValue(at('state'), (s) => onState(decode(s.val()))),
    onValue(at('presence'), (s) => onPresence(s.val() || {})),
  ];

  let myPresenceRef = null;
  if (user) {
    myPresenceRef = at(`presence/${user.uid}`);
    onDisconnect(myPresenceRef).remove();
    set(myPresenceRef, true);
    update(at('meta'), { lastActiveAt: Date.now() }).catch(() => {});
  }

  return () => {
    offs.forEach((off) => off());
    if (myPresenceRef) remove(myPresenceRef);
  };
};

/** Deletes the caller's own dead rooms - self-service, the house pattern. */
export const sweepMyRooms = async ({ user, now = Date.now(), database = db }) => {
  if (!database || !user) return { deleted: 0, pruned: 0 };
  let deleted = 0;
  let pruned = 0;
  const mine = await get(dbRef(database, `${USER_GAMES}/${user.uid}`));
  if (!mine.exists()) return { deleted, pruned };

  for (const [gameId, entry] of Object.entries(mine.val() || {})) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const metaSnap = await get(dbRef(database, `${GAMES}/${gameId}/meta`));
      if (!metaSnap.exists()) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`));
        pruned += 1;
        continue;
      }
      if (entry?.role !== 'host' && metaSnap.val().hostUid !== user.uid) continue;
      // eslint-disable-next-line no-await-in-loop
      const presenceSnap = await get(dbRef(database, `${GAMES}/${gameId}/presence`));
      const meta = metaSnap.val();
      if (!isDeadRoom(meta, presenceSnap.val() || {}, now)) continue;
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${GAMES}/${gameId}`));
      if (meta.roomCode) {
        // eslint-disable-next-line no-await-in-loop
        await remove(dbRef(database, `${CODES}/${meta.roomCode}`)).catch(() => {});
      }
      // eslint-disable-next-line no-await-in-loop
      await remove(dbRef(database, `${USER_GAMES}/${user.uid}/${gameId}`)).catch(() => {});
      deleted += 1;
    } catch {
      // Never let one room stop the sweep.
    }
  }
  return { deleted, pruned };
};
